import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';
import { exportToPDF, exportToExcel } from './utils/reportExport';
import ReportChart from './components/ReportChart';

function ReportViewer() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [report, setReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    try {
      setLoading(true);
      
      // Fetch report config
      const reportRes = await apiFetch(`/api/reports/${id}`);
      
      if (!reportRes.ok) {
        throw new Error('Report not found');
      }
      
      const reportData = await reportRes.json();
      
      if (reportData.error) {
        throw new Error(reportData.error);
      }
      
      setReport(reportData);

      // Auto-run the report
      await runReportNow();
    } catch (err) {
      console.error('Error loading report:', err);
      alert('Failed to load report: ' + err.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const runReportNow = async () => {
    try {
      setRunning(true);
      const res = await apiFetch(`/api/reports/${id}/run`);
      
      if (!res.ok) {
        throw new Error('Failed to execute report');
      }
      
      const result = await res.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      setData(result);
    } catch (err) {
      console.error('Error running report:', err);
      alert('Failed to run report: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const runReport = async () => {
    await runReportNow();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!data || !data.data || !sortConfig.key) return data?.data || [];

    const sorted = [...data.data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    return sorted;
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const exportToCSV = () => {
    if (!data || !data.data || data.data.length === 0) return;

    const rows = data.data;
    const headers = Object.keys(rows[0]);
    
    let csv = headers.join(',') + '\n';
    
    rows.forEach(row => {
      const values = headers.map(header => {
        const val = row[header];
        if (val === null || val === undefined) return '';
        const str = String(val);
        // Escape quotes and wrap in quotes if contains comma
        return str.includes(',') || str.includes('"') 
          ? `"${str.replace(/"/g, '""')}"` 
          : str;
      });
      csv += values.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.name.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    try {
      exportToPDF(report, data, report.layout_json || report.layout);
    } catch (error) {
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const handleExportExcel = () => {
    try {
      exportToExcel(report, data, report.layout_json || report.layout);
    } catch (error) {
      alert('Failed to export Excel: ' + error.message);
    }
  };

  // Calculate aggregate value for summary boxes
  const calculateAggregate = (rows, config) => {
    if (!rows || rows.length === 0) return 0;
    
    const { aggregate = 'count', field } = config;
    
    switch (aggregate) {
      case 'count':
        return rows.length;
      case 'sum':
        if (!field) return 0;
        return rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
      case 'avg':
        if (!field) return 0;
        const total = rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
        return total / rows.length;
      case 'min':
        if (!field) return 0;
        return Math.min(...rows.map(row => parseFloat(row[field]) || 0));
      case 'max':
        if (!field) return 0;
        return Math.max(...rows.map(row => parseFloat(row[field]) || 0));
      default:
        return 0;
    }
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (value > 100 || value < -100) {
        return '£' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      return value.toFixed(2);
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Report not found</p>
          <button
            onClick={() => navigate('/reports')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const sortedData = getSortedData();

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title={report.name}
        subtitle={report.description || 'Report Results'}
        backButton
        onBack={() => navigate('/reports')}
        backTitle="Back to Reports"
        sticky
        actionButton={
          <div className="flex space-x-2">
            <button
              onClick={() => navigate(`/reports/${id}/edit`)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700"
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => navigate(`/reports/${id}/design`)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700"
            >
              🎨 Design Layout
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!data || !data.data || data.data.length === 0}
              className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              title="Export as PDF (opens print dialog)"
            >
              📄 PDF
            </button>
            <button
              onClick={handleExportExcel}
              disabled={!data || !data.data || data.data.length === 0}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              title="Export as Excel (CSV format)"
            >
              📊 Excel
            </button>
            <button
              onClick={exportToCSV}
              disabled={!data || !data.data || data.data.length === 0}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50"
            >
              📥 CSV
            </button>
            <button
              onClick={runReport}
              disabled={running}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {running ? '⏳ Running...' : '🔄 Refresh'}
            </button>
          </div>
        }
      />

      {/* Results Summary */}
      {data && (
        <div className="bg-white border-b sticky top-[116px] z-10">
          <div className="max-w-full mx-auto px-6 py-3 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{data.rowCount}</span> rows returned
              {data.executionTime && (
                <span className="ml-4">
                  Executed in <span className="font-semibold">{data.executionTime}ms</span>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Layout (if designed) */}
      {data && report.layout_json && report.layout_json.components && report.layout_json.components.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          {report.layout_json.components.map((component, index) => {
            switch (component.type) {
              case 'header':
                return (
                  <div key={index} className="bg-gray-100 border-l-4 border-blue-600 p-6 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-900">{component.config.title || ''}</h2>
                    {component.config.subtitle && (
                      <p className="text-gray-600 mt-2">{component.config.subtitle}</p>
                    )}
                  </div>
                );

              case 'chart':
                return (
                  <div key={index} className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {component.config.title || `${component.config.chartType || 'Bar'} Chart`}
                    </h3>
                    <ReportChart 
                      data={data.data}
                      chartType={component.config.chartType || 'bar'}
                      config={component.config}
                    />
                  </div>
                );

              case 'text':
                return (
                  <div key={index} className="bg-white rounded-lg shadow p-6">
                    <div className="prose max-w-none">
                      {component.config.content}
                    </div>
                  </div>
                );

              case 'summary_box':
                const value = calculateAggregate(data.data, component.config);
                return (
                  <div key={index} className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 inline-block">
                    <div className="text-sm text-gray-600 mb-2">{component.config.label || 'Total'}</div>
                    <div className="text-3xl font-bold text-blue-900">{formatValue(value)}</div>
                  </div>
                );

              case 'divider':
                const dividerClass = component.config.style === 'dashed' ? 'border-dashed' :
                                   component.config.style === 'dotted' ? 'border-dotted' : '';
                return <hr key={index} className={`border-t-2 border-gray-300 ${dividerClass}`} />;

              default:
                return null;
            }
          })}
        </div>
      )}

      {/* Results Table */}
      <div className="max-w-full mx-auto px-6 py-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {running ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Running report...</p>
            </div>
          ) : !data ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg mb-2">No data yet</p>
              <button
                onClick={runReport}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Run Report
              </button>
            </div>
          ) : data.rowCount === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg mb-2">No results found</p>
              <p className="text-sm">Try adjusting the report filters or criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    {Object.keys(sortedData[0] || {}).map(key => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="text-left px-4 py-3 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                      >
                        {key}{getSortIndicator(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedData.map((row, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      {Object.values(row).map((value, i) => (
                        <td key={i} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {value !== null && value !== undefined ? String(value) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer Count */}
        {data && data.rowCount > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {sortedData.length} rows
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportViewer;
